/**
 * Cart Promo Auto-Add (Multi-Rule Support)
 * Automatically adds unlisted promo items based on multiple rules with priority
 */

(function() {
  // Check if feature is enabled
  if (!window.MinimogSettings || !window.MinimogSettings.cart_promo_auto_add_enable) {
    return;
  }
  
  const PROMO_PROPERTY_KEY = '_promo_auto_add';
  const DISCOUNT_CODE_KEY = 'minimog-discount-code';
  
  let isProcessing = false;
  let loadingIndicator = null;
  
  // Create loading indicator element
  function createLoadingIndicator() {
    const indicator = document.createElement('div');
    indicator.id = 'promo-loading-indicator';
    indicator.innerHTML = `
      <div style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.3); z-index: 99999; display: flex; align-items: center; justify-content: center;">
        <div style="background: white; padding: 24px 32px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); text-align: center;">
          <div style="width: 40px; height: 40px; border: 4px solid #f3f3f3; border-top: 4px solid #333; border-radius: 50%; margin: 0 auto 16px; animation: spin 1s linear infinite;"></div>
          <div style="font-size: 16px; color: #333; font-weight: 500;">Adding free item...</div>
        </div>
      </div>
      <style>
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      </style>
    `;
    return indicator;
  }
  
  function showLoading() {
    if (!loadingIndicator) {
      loadingIndicator = createLoadingIndicator();
      document.body.appendChild(loadingIndicator);
    }
  }
  
  function hideLoading() {
    if (loadingIndicator && loadingIndicator.parentNode) {
      loadingIndicator.parentNode.removeChild(loadingIndicator);
      loadingIndicator = null;
    }
  }
  
  // Get all promo rules and sort by priority (lower number = higher priority)
  const promoRules = (window.MinimogSettings.cart_promo_rules || [])
    .filter(rule => rule.variantId)
    .sort((a, b) => (a.priority || 0) - (b.priority || 0));
  
  // Skip if no valid rules
  if (promoRules.length === 0) {
    console.warn('Cart promo auto-add: No valid promo rules configured');
    return;
  }
  
  console.log('[Cart Promo] Loaded rules:', promoRules);
  
  /**
   * Get current cart from Shopify
   */
  function getCart() {
    return fetch(window.Shopify.routes.root + 'cart.json')
      .then(res => res.json());
  }
  
  /**
   * Get discount code from localStorage (cart addon stores it there)
   */
  function getCurrentDiscountCode() {
    return (localStorage.getItem(DISCOUNT_CODE_KEY) || '').toLowerCase();
  }
  
  /**
   * Find which promo rule (if any) should apply based on cart state
   * Returns the highest priority matching rule, or null if none match
   */
  function getMatchingPromoRule(cart) {
    const currentCode = getCurrentDiscountCode();
    const cartTotal = cart.total_price / 100;
    
    console.log('[Cart Promo] Checking rules:', {
      currentCode,
      cartTotal,
      totalPrice: cart.total_price,
      itemsOriginalPrice: cart.items_subtotal_price,
      originalTotalPrice: cart.original_total_price
    });
    
    // Find first rule that matches (sorted by priority)
    for (const rule of promoRules) {
      const codeMatches = currentCode && currentCode === rule.requiredCode.toLowerCase();
      const thresholdMet = cartTotal >= rule.threshold;
      
      console.log('[Cart Promo] Rule check:', {
        ruleName: rule.name,
        requiredCode: rule.requiredCode,
        codeMatches,
        threshold: rule.threshold,
        thresholdMet,
        matches: codeMatches && thresholdMet
      });
      
      if (codeMatches && thresholdMet) {
        console.log('[Cart Promo] Rule matched:', rule.name);
        return rule;
      }
    }
    
    console.log('[Cart Promo] No matching rule');
    return null;
  }
  
  /**
   * Find all promo line items in cart (from any rule)
   */
  function findAllPromoLineItems(cart) {
    return cart.items.filter(item => 
      item.properties && 
      item.properties[PROMO_PROPERTY_KEY] === 'true'
    );
  }
  
  /**
   * Find specific promo line item by variant ID
   */
  function findPromoLineItem(cart, variantId) {
    return cart.items.find(item => 
      item.variant_id.toString() === variantId.toString() &&
      item.properties && 
      item.properties[PROMO_PROPERTY_KEY] === 'true'
    );
  }
  
  /**
   * Add promo item to cart
   */
  function addPromoItem(rule) {
    if (isProcessing) return Promise.resolve();
    isProcessing = true;
    showLoading();
    
    const properties = {
      [PROMO_PROPERTY_KEY]: 'true',
      '_promo_rule_id': rule.id
    };
    
    // Add label if configured
    if (rule.label) {
      properties['Promo'] = rule.label;
    }
    
    const body = JSON.stringify({
      items: [{
        id: rule.variantId,
        quantity: 1,
        properties: properties
      }]
    });
    
    console.log('[Cart Promo] Adding item:', { variantId: rule.variantId, properties });
    
    return fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: body
    })
    .then(res => {
      console.log('[Cart Promo] Add response status:', res.status);
      if (!res.ok) {
        return res.text().then(text => {
          console.error('[Cart Promo] Add failed response:', text);
          throw new Error(`Failed to add item: ${res.status} ${res.statusText}`);
        });
      }
      return res.text().then(text => {
        try {
          return JSON.parse(text);
        } catch (e) {
          console.error('[Cart Promo] Invalid JSON response:', text.substring(0, 200));
          throw new Error('Invalid response from server');
        }
      });
    })
    .then(response => {
      console.log('[Cart Promo] Item added successfully:', response);
      // Verify item was added by fetching cart
      return getCart();
    })
    .then(cart => {
      const itemAdded = findPromoLineItem(cart, rule.variantId);
      if (itemAdded) {
        console.log('[Cart Promo] Verified item in cart, reloading...');
        // Keep loading indicator visible during reload
        window.location.reload();
      } else {
        console.error('[Cart Promo] Item not found in cart after add');
        hideLoading();
        isProcessing = false;
      }
    })
    .catch(err => {
      console.error('[Cart Promo] Failed to add promo item:', err);
      hideLoading();
      isProcessing = false;
    });
  }
  
  /**
   * Remove promo item from cart
   */
  function removePromoItem(lineIndex) {
    if (isProcessing) return Promise.resolve();
    isProcessing = true;
    
    const body = JSON.stringify({
      line: lineIndex,
      quantity: 0
    });
    
    return fetch(window.MinimogSettings.routes.cart_change_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: body
    })
    .then(() => {
      // Reload page to update cart
      window.location.reload();
    })
    .catch(err => {
      console.error('Failed to remove promo item:', err);
      isProcessing = false;
    });
  }
  
  /**
   * Check and update promo items based on rules
   */
  function checkAndUpdatePromo() {
    if (isProcessing) {
      console.log('[Cart Promo] Already processing, skipping...');
      return;
    }
    
    console.log('[Cart Promo] Checking and updating promo...');
    
    getCart().then(cart => {
      console.log('[Cart Promo] Cart data:', cart);
      
      const matchingRule = getMatchingPromoRule(cart);
      const allPromoItems = findAllPromoLineItems(cart);
      
      console.log('[Cart Promo] State:', {
        matchingRule: matchingRule ? matchingRule.name : 'none',
        promoItemsCount: allPromoItems.length
      });
      
      if (matchingRule) {
        // A rule matches - ensure correct promo item is in cart
        const correctPromoItem = findPromoLineItem(cart, matchingRule.variantId);
        
        // Remove any other promo items (from different rules)
        const wrongPromoItems = allPromoItems.filter(item => 
          item.variant_id.toString() !== matchingRule.variantId.toString()
        );
        
        if (wrongPromoItems.length > 0) {
          console.log('[Cart Promo] Removing wrong promo items');
          // Remove wrong promo items first
          const lineIndex = cart.items.findIndex(item => item.key === wrongPromoItems[0].key) + 1;
          removePromoItem(lineIndex);
        } else if (!correctPromoItem) {
          console.log('[Cart Promo] Adding promo item:', matchingRule.variantId);
          // Add the correct promo item
          addPromoItem(matchingRule);
        } else {
          console.log('[Cart Promo] Correct promo item already in cart');
        }
      } else {
        // No rule matches - remove all promo items
        if (allPromoItems.length > 0) {
          console.log('[Cart Promo] Removing all promo items');
          const lineIndex = cart.items.findIndex(item => item.key === allPromoItems[0].key) + 1;
          removePromoItem(lineIndex);
        } else {
          console.log('[Cart Promo] No action needed');
        }
      }
    }).catch(err => {
      console.error('[Cart Promo] Error:', err);
    });
  }
  
  /**
   * Handle cart update events
   */
  function handleCartUpdate(event) {
    // Skip processing during our own updates
    if (isProcessing) {
      console.log('[Cart Promo] Skipping cart update - processing in progress');
      return;
    }
    
    // Skip if this update came from our own add
    if (event && event.source === 'promo-add') {
      console.log('[Cart Promo] Skipping cart update - from promo add');
      return;
    }
    
    // Small delay to let cart and localStorage stabilize
    setTimeout(() => {
      if (!isProcessing) {
        checkAndUpdatePromo();
      }
    }, 300);
  }
  
  // Listen for cart updates
  if (window.MinimogEvents && window.MinimogTheme) {
    window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.cartUpdate, handleCartUpdate);
  }
  
  // Listen for discount code save button click
  document.addEventListener('click', (e) => {
    const saveButton = e.target.closest('.m-cart-addon--save[data-action="coupon"]');
    if (saveButton && !isProcessing) {
      // Small delay to let localStorage update, then check if promo should be added
      setTimeout(() => {
        if (!isProcessing) {
          checkAndUpdatePromo();
        }
      }, 100);
    }
  }, true);
  
  // Initial check on page load
  document.addEventListener('DOMContentLoaded', () => {
    checkAndUpdatePromo();
  });
})();
